/**
 * @fileoverview Componente responsável por renderizar uma coluna do quadro kanban.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import InputBase from "@material-ui/core/InputBase";
import KanbanCard from "./KanbanCard";

import { Plus, Trash2, Inbox, Settings2, CheckCircle2 } from "lucide-react";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    column: {
      width: 296,
      minWidth: 296,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      maxHeight: "100%",
      borderRadius: 12,
      background: dark
        ? "rgba(14,16,22,0.85)"
        : "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
      border: dark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      boxShadow: dark
        ? "0 1px 2px rgba(0,0,0,0.30)"
        : "0 1px 2px rgba(15,23,42,0.04)",
      transition: "border-color .15s ease, box-shadow .15s ease",
    },
    columnDragOver: {
      borderColor: dark ? "rgba(99,102,241,0.32)" : "rgba(61,92,253,0.42)",
      boxShadow: dark
        ? "0 0 0 1px rgba(99,102,241,0.22), 0 8px 24px -6px rgba(0,0,0,0.45)"
        : "0 0 0 1px rgba(61,92,253,0.32), 0 8px 32px -6px rgba(61,92,253,0.14)",
    },
    "@global": {
      ".touch-drag-over": {
        boxShadow: dark
          ? "0 0 0 1px rgba(99,102,241,0.22), 0 8px 24px -6px rgba(0,0,0,0.45)"
          : "0 0 0 1px rgba(61,92,253,0.32), 0 8px 32px -6px rgba(61,92,253,0.14)",
      },
    },
    columnDragging: {
      opacity: 0.55,
      transform: "rotate(1deg)",
    },

    header: {
      padding: "12px 12px 10px",
      display: "flex",
      alignItems: "center",
      gap: 8,
      cursor: "grab",
      position: "sticky",
      top: 0,
      zIndex: 2,
      borderBottom: dark
        ? "1px solid rgba(255,255,255,0.04)"
        : "1px solid rgba(15,23,42,0.04)",
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      background: "inherit",
      "&:active": { cursor: "grabbing" },
    },
    // Só leitura (quadro compartilhado por link): sem arrastar nem editar.
    headerReadOnly: {
      cursor: "default",
      "&:active": { cursor: "default" },
    },
    statusNameReadOnly: {
      cursor: "default",
      "&:hover": { background: "transparent" },
    },
    emptyReadOnly: {
      cursor: "default",
      "&:hover": {
        borderColor: dark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.10)",
        background: "transparent",
        color: dark ? "#6B7280" : "#94A3B8",
      },
    },
    colorBar: {
      width: 3,
      height: 16,
      borderRadius: 2,
      flexShrink: 0,
    },
    statusName: {
      fontSize: "0.78rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: dark ? "#E8EAED" : "#0F172A",
      flex: 1,
      wordBreak: "break-word",
      cursor: "text",
      borderRadius: 5,
      padding: "2px 6px",
      transition: "background .12s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      },
    },
    statusNameInput: {
      fontSize: "0.78rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: dark ? "#E8EAED" : "#0F172A",
      flex: 1,
      padding: "2px 6px",
      borderRadius: 5,
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: dark
        ? "1px solid rgba(99,102,241,0.35)"
        : "1px solid rgba(61,92,253,0.32)",
    },
    badge: {
      fontSize: "0.7rem",
      fontWeight: 700,
      color: dark ? "#9CA3AF" : "#64748B",
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      borderRadius: 999,
      padding: "2px 8px",
      minWidth: 24,
      textAlign: "center",
      lineHeight: 1.3,
      flexShrink: 0,
    },
    // Marca de coluna de conclusão ao lado do nome.
    doneIcon: {
      color: "#22c55e",
      flexShrink: 0,
      display: "inline-flex",
    },
    iconBtn: {
      width: 24,
      height: 24,
      padding: 0,
      borderRadius: 6,
      color: dark ? "#6B7280" : "#94A3B8",
      transition: "background .12s, color .12s",
      "&:hover": {
        color: dark ? "#A5B4FC" : "#3D5CFD",
        background: dark ? "rgba(99,102,241,0.08)" : "rgba(61,92,253,0.08)",
      },
    },
    deleteBtn: {
      width: 24,
      height: 24,
      padding: 0,
      borderRadius: 6,
      color: dark ? "rgba(255,255,255,0.20)" : "#CBD5E1",
      transition: "background .12s, color .12s",
      "&:hover": {
        color: "#ef4444",
        background: "rgba(239,68,68,0.10)",
      },
    },

    cardList: {
      flex: 1,
      overflowY: "auto",
      overflowX: "hidden",
      padding: "8px 8px 10px",
      minHeight: 60,
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)",
        borderRadius: 3,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.14)",
      },
    },

    emptyPlaceholder: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      height: 110,
      padding: "12px 16px",
      borderRadius: 10,
      border: dark
        ? "1px dashed rgba(255,255,255,0.07)"
        : "1px dashed rgba(15,23,42,0.10)",
      background: "transparent",
      transition: "border-color .15s, background .15s, color .15s",
      cursor: "pointer",
      color: dark ? "#6B7280" : "#94A3B8",
      "&:hover": {
        borderColor: dark ? "rgba(99,102,241,0.24)" : "rgba(61,92,253,0.24)",
        background: dark ? "rgba(99,102,241,0.03)" : "rgba(61,92,253,0.03)",
        color: dark ? "#9CA3AF" : "#475569",
      },
    },
    emptyIcon: {
      opacity: 0.6,
      marginBottom: 2,
    },
    emptyTitle: {
      fontSize: "0.78rem",
      fontWeight: 600,
      letterSpacing: "0.01em",
    },
    emptyHint: {
      fontSize: "0.7rem",
      opacity: 0.75,
      textAlign: "center",
      lineHeight: 1.35,
    },

    dropIndicator: {
      height: 3,
      borderRadius: 2,
      background: dark ? "#818CF8" : "#3D5CFD",
      margin: "3px 6px",
      transition: "opacity 0.15s",
      boxShadow: dark
        ? "0 0 10px rgba(129,140,248,0.45)"
        : "0 0 10px rgba(61,92,253,0.4)",
    },
  };
});

const KanbanColumn = ({
  status,
  leads,
  triageByLead,
  onDropCard,
  onDragColumnStart,
  onDragColumnOver,
  onDragColumnDrop,
  onViewLead,
  onStartConversation,
  onAddLead,
  onEditColumn,
  onOpenColumnSettings,
  onDeleteColumn,
  isDraggingColumn,
  readOnly = false,
  emptyText = "Sem cards nesta etapa",
}) => {
  const classes = useStyles();
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropIndex, setDropIndex] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(status.name);
  const inputRef = useRef(null);
  const cardListRef = useRef(null);
  const columnRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleTouchDrop = useCallback((e) => {
    const { leadId, fromStatusId, toStatusId, targetIndex } = e.detail;
    onDropCard(leadId, fromStatusId, toStatusId, targetIndex);
  }, [onDropCard]);

  useEffect(() => {
    const el = columnRef.current;
    if (!el) return;
    el.addEventListener("touch-drop", handleTouchDrop);
    return () => el.removeEventListener("touch-drop", handleTouchDrop);
  }, [handleTouchDrop]);

  const handleStartEdit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setEditName(status.name);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== status.name && onEditColumn) {
      onEditColumn(status.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === "Escape") {
      setEditName(status.name);
      setIsEditing(false);
    }
  };

  const getDropIndex = (e) => {
    const listEl = cardListRef.current;
    if (!listEl) return 0;
    const cards = Array.from(listEl.querySelectorAll("[data-card-id]"));
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (e.clientY < midY) return i;
    }
    return cards.length;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    const isCard = e.dataTransfer.types.includes("leadid");
    if (isCard) {
      e.dataTransfer.dropEffect = "move";
      setIsDragOver(true);
      setDropIndex(getDropIndex(e));
    }
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
      setDropIndex(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const targetIndex = getDropIndex(e);
    setIsDragOver(false);
    setDropIndex(null);
    const leadId = e.dataTransfer.getData("leadId");
    const fromStatusId = e.dataTransfer.getData("fromStatusId");
    if (leadId && fromStatusId) {
      onDropCard(parseInt(leadId), parseInt(fromStatusId), status.id, targetIndex);
    }
  };

  const handleColumnDragStart = (e) => {
    e.dataTransfer.setData("columnId", status.id);
    e.dataTransfer.effectAllowed = "move";
    onDragColumnStart(status.id);
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes("columnid")) {
      onDragColumnOver(status.id);
    }
  };

  const handleColumnDrop = (e) => {
    if (e.dataTransfer.types.includes("columnid")) {
      e.preventDefault();
      const columnId = e.dataTransfer.getData("columnId");
      if (columnId) {
        onDragColumnDrop(parseInt(columnId), status.id);
      }
    }
  };

  const columnClasses = [
    classes.column,
    isDragOver ? classes.columnDragOver : "",
    isDraggingColumn ? classes.columnDragging : "",
  ].filter(Boolean).join(" ");

  // Só leitura: nada de arrastar card ou coluna, nem de editar o nome.
  const dropHandlers = readOnly
    ? {}
    : { onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop };
  const headerDragHandlers = readOnly
    ? {}
    : { draggable: true, onDragStart: handleColumnDragStart, onDragOver: handleColumnDragOver, onDrop: handleColumnDrop };

  return (
    <div
      ref={columnRef}
      className={columnClasses}
      data-column-id={status.id}
      {...dropHandlers}
    >
      <div
        className={`${classes.header}${readOnly ? ` ${classes.headerReadOnly}` : ""}`}
        {...headerDragHandlers}
      >
        <div className={classes.colorBar} style={{ backgroundColor: status.color }} />
        {readOnly ? (
          <Typography className={`${classes.statusName} ${classes.statusNameReadOnly}`} title={status.name}>
            {status.name}
          </Typography>
        ) : isEditing ? (
          <InputBase
            inputRef={inputRef}
            className={classes.statusNameInput}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSaveEdit}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            inputProps={{ maxLength: 50 }}
          />
        ) : (
          <Typography
            className={classes.statusName}
            title={`${status.name} — clique para editar`}
            onClick={handleStartEdit}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {status.name}
          </Typography>
        )}
        {status.isCompleted && (
          <Tooltip title="Coluna de conclusão: cards aqui contam como concluídos e o prazo deixa de contar">
            <span className={classes.doneIcon} aria-label="Coluna de conclusão">
              <CheckCircle2 size={14} strokeWidth={2.25} />
            </span>
          </Tooltip>
        )}
        <span className={classes.badge}>{leads.length}</span>
        {!readOnly && (
        <Tooltip title="Novo card">
          <IconButton
            className={classes.iconBtn}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAddLead(status.id);
            }}
            aria-label="Adicionar card"
          >
            <Plus size={15} strokeWidth={2} />
          </IconButton>
        </Tooltip>
        )}
        {!readOnly && onOpenColumnSettings && (
          <Tooltip title="Configurar coluna">
            <IconButton
              className={classes.iconBtn}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpenColumnSettings(status.id);
              }}
              aria-label="Configurar coluna"
            >
              <Settings2 size={14} strokeWidth={1.9} />
            </IconButton>
          </Tooltip>
        )}
        {!readOnly && (
        <Tooltip title="Excluir coluna">
          <IconButton
            className={classes.deleteBtn}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onDeleteColumn) onDeleteColumn(status.id);
            }}
            aria-label="Excluir coluna"
          >
            <Trash2 size={14} strokeWidth={1.75} />
          </IconButton>
        </Tooltip>
        )}
      </div>

      <div className={classes.cardList} ref={cardListRef} data-card-list>
        {leads.length === 0 && readOnly ? (
          <div className={`${classes.emptyPlaceholder} ${classes.emptyReadOnly}`}>
            <Inbox size={18} className={classes.emptyIcon} strokeWidth={1.5} />
            <span className={classes.emptyTitle}>{emptyText}</span>
          </div>
        ) : leads.length === 0 ? (
          <div
            className={classes.emptyPlaceholder}
            onClick={() => onAddLead(status.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onAddLead(status.id);
              }
            }}
            aria-label="Sem cards — clique para adicionar"
          >
            <Inbox size={18} className={classes.emptyIcon} strokeWidth={1.5} />
            <span className={classes.emptyTitle}>Sem cards nesta etapa</span>
            <span className={classes.emptyHint}>
              Arraste um card ou clique para criar
            </span>
          </div>
        ) : (
          leads.map((lead, index) => (
            <React.Fragment key={lead.id}>
              {isDragOver && dropIndex === index && (
                <div className={classes.dropIndicator} />
              )}
              <div data-card-id={lead.id}>
                <KanbanCard
                  lead={lead}
                  statusId={status.id}
                  isCompleted={!!status.isCompleted}
                  triage={triageByLead?.[lead.id] || null}
                  onView={onViewLead}
                  onStartConversation={onStartConversation}
                  readOnly={readOnly}
                />
              </div>
            </React.Fragment>
          ))
        )}
        {isDragOver && dropIndex === leads.length && leads.length > 0 && (
          <div className={classes.dropIndicator} />
        )}
      </div>
    </div>
  );
};

export default KanbanColumn;
